import React, { useEffect, useState, useRef, useCallback } from 'react';
import { base44 } from '@/api/base44Client';
import { supabase } from '@/lib/supabase';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { MapPin, Shield, Swords, Crown, AlertTriangle, PlusCircle, Loader2 } from 'lucide-react';
import ShareCard from '@/components/ShareCard';
import { useOptimizedGeolocation } from '@/hooks/useOptimizedGeolocation';
import { usePullToRefresh } from '@/components/usePullToRefresh';

const FEED_SCROLL_KEY = 'zno:feedScroll';

const pn = (e) => e.playground_name || 'un terrain';

const EVENT_CONFIG = {
  created: {
    icon: PlusCircle,
    color: 'text-green-400',
    bg: 'bg-green-500/10',
    label: (e) => `Nouveau terrain créé : ${pn(e)}`,
  },
  captured: {
    icon: Swords,
    color: 'text-red-400',
    bg: 'bg-red-500/10',
    label: (e) => `@${e.user_name} fait tomber ${pn(e)}`,
    detail: (e) =>
      e.score_attacker != null
        ? `${e.score_attacker} - ${e.score_defender} vs @${e.opponent_name}`
        : e.opponent_name ? `vs @${e.opponent_name}` : null,
  },
  defended: {
    icon: Shield,
    color: 'text-blue-400',
    bg: 'bg-blue-500/10',
    label: (e) => `@${e.user_name} défend ${pn(e)}`,
    detail: (e) =>
      e.score_attacker != null
        ? `${e.score_defender} - ${e.score_attacker} vs @${e.opponent_name}`
        : e.opponent_name ? `vs @${e.opponent_name}` : null,
  },
  fallen_no_defense: {
    icon: AlertTriangle,
    color: 'text-amber-400',
    bg: 'bg-amber-500/10',
    label: (e) => `@${e.user_name} prend ${pn(e)} sans défense`,
  },
  record: {
    icon: Crown,
    color: 'text-purple-400',
    bg: 'bg-purple-500/10',
    label: (e) => `Record battu sur ${pn(e)}`,
  },
};

function ensureUTC(dateStr) {
  if (!dateStr) return dateStr;
  if (typeof dateStr === 'string' && !dateStr.endsWith('Z') && !dateStr.includes('+')) return dateStr + 'Z';
  return dateStr;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(ensureUTC(dateStr)).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return "à l'instant";
  if (minutes < 60) return `il y a ${minutes}min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours}h`;
  return `il y a ${Math.floor(hours / 24)}j`;
}

export default function LocalFeed({ onClose, userLocation: propUserLocation, onNavigate }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { getLocation } = useOptimizedGeolocation();
  const [userLocation, setUserLocation] = useState(propUserLocation || null);
  const scrollContainerRef = useRef(null);

  // Pull-to-refresh: invalidate localFeed query
  const { isPulling, pullDistance } = usePullToRefresh(
    useCallback(async () => {
      await queryClient.invalidateQueries({ queryKey: ['localFeed'] });
    }, [queryClient]),
    !onClose // Only enabled in standalone mode (routed, not overlay)
  );

  // Get user location if not provided as prop (standalone mode)
  useEffect(() => {
    if (!propUserLocation) {
      getLocation({ useCache: true, timeout: 3000 })
        .then(location => {
          setUserLocation({ lat: location.lat, lng: location.lng });
        })
        .catch(err => {
          console.warn('Location failed:', err);
        });
    }
  }, [propUserLocation, getLocation]);

  // Standalone mode - full page without close button
  const isStandalone = !onClose;

  // Restore scroll position on mount
  useEffect(() => {
    try {
      const savedScroll = sessionStorage.getItem(FEED_SCROLL_KEY);
      if (savedScroll) {
        const scrollY = parseInt(savedScroll, 10);
        if (!isNaN(scrollY)) {
          requestAnimationFrame(() => {
            if (isStandalone) {
              window.scrollTo(0, scrollY);
            } else if (scrollContainerRef.current) {
              scrollContainerRef.current.scrollTop = scrollY;
            }
          });
        }
      }
    } catch {
      // sessionStorage not available
    }
  }, [isStandalone]);

  // Save scroll position on scroll (overlay mode uses container, standalone uses window)
  const handleScroll = useCallback((e) => {
    try {
      sessionStorage.setItem(FEED_SCROLL_KEY, String(e.target.scrollTop));
    } catch {
      // sessionStorage not available
    }
  }, []);

  // Window scroll listener for standalone mode
  useEffect(() => {
    if (!isStandalone) return;
    const onWindowScroll = () => {
      try {
        sessionStorage.setItem(FEED_SCROLL_KEY, String(window.scrollY));
      } catch {
        // sessionStorage not available
      }
    };
    window.addEventListener('scroll', onWindowScroll, { passive: true });
    return () => window.removeEventListener('scroll', onWindowScroll);
  }, [isStandalone]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['localFeed', userLocation?.lat, userLocation?.lng],
    queryFn: async () => {
      if (!userLocation?.lat) return { events: [] };
      const { data: events } = await supabase.from('court_events').select('*').order('created_date', { ascending: false }).limit(50);
      return { events: events || [] };
    },
    enabled: !!userLocation?.lat,
    refetchInterval: 60000,
    retry: 1,
  });

  const events = data?.events || [];

  const handleNavigate = (playgroundId) => {
    if (onNavigate) {
      onNavigate(playgroundId);
    } else {
      navigate(`/court/${playgroundId}`);
    }
  };

  return (
    <div
      ref={scrollContainerRef}
      onScroll={!isStandalone ? handleScroll : undefined}
      className={isStandalone ? "min-h-full bg-[#0A0B10]" : "fixed inset-0 z-50 bg-[#0A0B10] overflow-auto"}
    >
      {/* Header */}
      <div className="sticky top-0 z-10 bg-[#0A0B10]/95 backdrop-blur-sm border-b border-white/5 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MapPin className="w-5 h-5 text-[#6E2BFF]" />
          <h2 className="text-white font-bold text-lg">Feed Local</h2>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-white p-1">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Pull-to-refresh indicator */}
      {isPulling && (
        <div
          className="flex items-center justify-center transition-all duration-150"
          style={{ height: Math.min(pullDistance, 80), overflow: 'hidden' }}
        >
          <Loader2
            className={`w-5 h-5 text-[#6E2BFF] ${pullDistance > 80 ? 'animate-spin' : ''}`}
            style={{ opacity: Math.min(pullDistance / 80, 1) }}
          />
        </div>
      )}

      <div className="p-4 space-y-3 pb-20">
        {isLoading && !isPulling && (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-6 h-6 text-[#6E2BFF] animate-spin" />
          </div>
        )}

        {!isLoading && !userLocation && (
          <div className="text-center py-12 text-gray-500">
            <MapPin className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">Localisation requise pour afficher le feed.</p>
          </div>
        )}

        {!isLoading && userLocation && (isError || events.length === 0) && (
          <div className="text-center py-12 text-gray-500">
            <MapPin className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">{isError ? 'Erreur de chargement.' : 'Aucune activité récente autour de toi.'}</p>
          </div>
        )}

        {events.map((event) => {
          const config = EVENT_CONFIG[event.event_type];
          if (!config) return null;
          const Icon = config.icon;
          const detail = config.detail?.(event);

          return (
            <div
              key={event.id}
              onClick={() => event.playground_id && handleNavigate(event.playground_id)}
              className={`${config.bg} rounded-xl p-3 flex items-start gap-3 border border-white/5 ${event.playground_id ? 'cursor-pointer' : ''}`}
            >
              <div className={`mt-0.5 ${config.color}`}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium leading-tight">
                  {config.label(event)}
                </p>
                {detail && (
                  <p className="text-gray-500 text-xs mt-0.5">{detail}</p>
                )}
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="text-gray-600 text-xs whitespace-nowrap">
                  {timeAgo(event.created_date)}
                </span>
                <ShareCard
                  event_type={event.event_type}
                  playground_name={event.playground_name}
                  playground_city={event.playground_city}
                  user_name={event.user_name}
                  opponent_name={event.opponent_name}
                  score_attacker={event.score_attacker}
                  score_defender={event.score_defender}
                  className="text-gray-500 hover:text-white p-0 h-6 w-6"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
